import React, { createContext, useContext, useState, useEffect } from "react";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  userEmail?: string;
  isStaff: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  userEmail: undefined,
  isStaff: false,
});

export const useAuthContext = () => useContext(AuthContext);

/**
 * Hook to manage Cognito authentication state.
 * Checks if user is authenticated via Cognito and provides user email.
 */
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);

  useEffect(() => {
    // When a dev API key is set, skip Cognito entirely
    if (import.meta.env.VITE_API_KEY) {
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        await getCurrentUser();
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload.email as string | undefined;
        setIsAuthenticated(true);
        setUserEmail(email);
      } catch (error) {
        setIsAuthenticated(false);
        setUserEmail(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Re-check when Amplify says the session changed. Without this, an instance
    // mounted before sign-in — which is every instance above the router, e.g.
    // AuthProvider itself — stays stuck at "not authenticated" for the whole
    // session, because the check above runs once on mount. Pages happened not to
    // notice (ProtectedRoute mounts after login and runs its own check), but
    // anything reading the *context* saw a value that was only ever right after
    // a full page load.
    return Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn" || payload.event === "signedOut") {
        checkAuth();
      }
    });
  }, []);

  return { isAuthenticated, isLoading, userEmail };
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, userEmail } = useAuth();
  // Composo staff are identified by their email domain, mirroring the backend
  // (`APIKey.is_staff` / `_check_admin`). Drives the staff-only "view as
  // customer" entry point; the backend re-checks before honoring impersonation.
  const isStaff = (userEmail ?? "").toLowerCase().endsWith("@composo.ai");
  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, userEmail, isStaff }}
    >
      {children}
    </AuthContext.Provider>
  );
};
